import { ApprovalProcess, Employee, Expense, RightEnum, Role, SystemRightEnum } from "@finway-group/shared/lib/models"
import { ExpensePaymentOptionEnum } from "@finway-group/shared/lib/models/expense/expensePaymentOption.enum"
import { TFunction } from "i18next"
import moment from "moment-timezone"
import { PROCESS_ENV } from "~Shared/config/env.utils"
import { getRemainingApprovers } from "~Shared/utils/approvalProcess.utils"
import { formatDate } from "~Shared/utils/date.utils"
import { isRightGranted } from "~Shared/utils/helper.utils"

export const getEmployeesByIdsInAbsenceOfLeave = (employeeIds: Array<string>, employees: Array<Employee>) =>
    employees.filter((employee: Employee) => {
        if (!employeeIds.includes(employee.id)) {
            return false
        }
        return employee.isInAbsenceOfLeave()
    })

export const getAbsentApprovers = (approvalProcess: ApprovalProcess, employees: Array<Employee>) => {
    const remainingApproversIds = getRemainingApprovers(approvalProcess).map((approvalProcess) => approvalProcess.approver?._id)
    const absentApprovers = getEmployeesByIdsInAbsenceOfLeave(remainingApproversIds, employees)
    return absentApprovers
}

/* Admin is considered absent if it is currently or in the next in absence of leave */
export const isAdminAbsent = (startDate: Date, endDate: Date) =>
    moment().isBetween(startDate, endDate, "day", "[]") || moment(startDate).isBetween(moment(), moment().add(1, "week"), "day", "[]")

export const getAvailableReplacementsForAbsentApprover = (
    entity: Pick<Expense, "requestedBy"> & Partial<Pick<Expense, "paymentOption">>,
    employees: Array<Employee>,
    rolesMap: Map<string, Role>,
) => {
    const availableReplacementsForAbsentApprover = employees.filter((employee) => {
        const { activeCompanyProfile, id, email, absence } = employee
        if (
            email === PROCESS_ENV.FINWAY_ADMIN_EMAIL ||
            id === entity.requestedBy._id ||
            !isRightGranted(rolesMap.get(activeCompanyProfile.roleId)?.rights, RightEnum.EXPENSE__ALL__APPROVE)
        ) {
            return false
        }
        if (entity?.paymentOption === ExpensePaymentOptionEnum.SMART_CARD) {
            // TODO: use employee class method isEnrolledInCardProgramOfCompany when 6.3 shared is promoted to master.
            const isNotEnrolledInCompanyCardProgram = !activeCompanyProfile.weavrData?.userId
            if (isNotEnrolledInCompanyCardProgram) {
                return false
            }
        }

        return !absence || !isAdminAbsent(absence.startDate, absence.endDate)
    })

    return availableReplacementsForAbsentApprover
}

export const formatEmployeeAbsenceDate = (employee: Employee) =>
    `${moment(employee.absence?.startDate).format("Do MMM, YYYY")} - ${moment(employee.absence?.endDate).format("Do MMM, YYYY")}`

export const isEmployeeInFutureAbsent = (employee: Employee) => (employee.absence ? moment().isBefore(employee.absence?.startDate) : false)

export const employeeAbsenceInfo = (employee: Employee, t: TFunction): string | undefined => {
    const isEmployeeAbsent = employee.isInAbsenceOfLeave()
    if (isEmployeeAbsent) {
        const daysLeft = employee.inAbsenceOfLeaveFor()
        return `${t("label:employee_absence.back_in")} ${t("label:employee_absence.day", { count: daysLeft })}`
    }

    const isFutureAbsent = isEmployeeInFutureAbsent(employee)
    if (isFutureAbsent) {
        return formatEmployeeAbsenceDate(employee)
    }
    return undefined
}

export const isSelfAdministrationOfRoleAllowed = (user: Employee, roleId: string) => !userHasAllSystemRights(user) || !user.companyProfiles?.map((cp) => cp.roleId).includes(roleId)

export const isSysAdminRightsRevocationDenied = ({ employee, sysAdminsCount, isDeletion = false }: { employee: Employee; sysAdminsCount: number; isDeletion?: boolean }) => {
    const sysAdminCountBelowOrEqualLimit = sysAdminsCount <= 2
    const nonDeletedCompanyProfilesCount = employee.companyProfiles?.filter((cp) => !cp.deleted).length ?? 0
    return sysAdminCountBelowOrEqualLimit && userHasAllSystemRights(employee) && (isDeletion ? nonDeletedCompanyProfilesCount <= 1 : true)
}

export const userHasAllSystemRights = (employee?: Employee) => (employee?.systemRights?.length ?? 0) === Object.values(SystemRightEnum).length

export const filterEmployees = (
    employees: Array<Employee>,
    selectedCompanyId: string,
    excludeDeleted: boolean = false,
    includeAutoApprover: boolean = false,
    searchValue?: string,
): Array<Employee> => {
    // Construct an array of employees, throw employee if there is no suitable company profile for the selected company id
    const employeesOfCompany = employees
        .map((employee) => {
            const employeeObject = new Employee(employee)
            const activeCompanyProfile = employeeObject.getUserCompanyProfile(selectedCompanyId)
            if (!activeCompanyProfile) {
                return undefined
            }
            employeeObject.activeCompanyProfile = activeCompanyProfile
            return employeeObject
        })
        .filter((e) => !!e) as Array<Employee>

    const filtersToApply: Array<(e: Employee) => boolean> = []

    if (!includeAutoApprover) {
        filtersToApply.push((employee) => employee.email !== PROCESS_ENV.FINWAY_ADMIN_EMAIL)
    }
    if (excludeDeleted) {
        filtersToApply.push((employee) => !!employee.activeCompanyProfile.companyId && !employee.activeCompanyProfile.deleted)
    }
    if (searchValue) {
        filtersToApply.push(
            (employee) => employee.getFullName().toLowerCase().includes(searchValue.toLowerCase()) || employee.email.toLowerCase().includes(searchValue.toLowerCase()),
        )
    }

    return employeesOfCompany.filter((e) => filtersToApply.every((filterFn) => filterFn(e)))
}

export const getSubstitutionTooltipMessage = (employee: Employee, loggedInUserId: string, t: TFunction) => {
    if (!employee.substitutedBy || !employee.absence) {
        return undefined
    }

    if (employee.substitutedBy.id === loggedInUserId) {
        return t(`tooltips:employee_absence.substituted_by_you_with_timeframe`, {
            approver: employee.getFullName(),
            startDate: formatDate(employee.absence.startDate),
            endDate: formatDate(employee.absence.endDate),
        })
    }

    return t(`tooltips:employee_absence.substituted_by_with_timeframe`, {
        approver: employee.id === loggedInUserId ? t("tooltips:employee_absence.approver_name_you") : employee.getFullName(),
        substitutedBy: `${employee.substitutedBy.firstName} ${employee.substitutedBy.lastName}`,
        startDate: formatDate(employee.absence.startDate),
        endDate: formatDate(employee.absence.endDate),
    })
}

export const createEmployeeNotDeletedFilter =
    ({ excludedUserIds }: { excludedUserIds: Array<string> }) =>
    (employee: Employee) =>
        !employee.activeCompanyProfile?.deleted || excludedUserIds.includes(employee.id)
